import {
  AbstractControl,
  UntypedFormControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';

import { hasNoValue, hasValue } from '@common';
import {
  CountryIsoNumber,
  CountryPostalCodeFormat,
  CountryTelephoneFormat,
} from '@i18n';
import { isAfter, isBefore, isValid } from 'date-fns';
import { ALPHABETICAL_REG_EXP } from '../../constants/alphabetical-reg-exp.constant';
import { EMAIL_ADDRESS_REG_EXP } from '../../constants/email-address-reg-exp.constant';
import { IBAN_REG_EXP } from '../../constants/iban-reg-exp.constant';
import { RESTRICTED_CHARACTERS_REG_EXP } from '../../constants/restricted-characters-reg-exp.constant';
import { FormControlValidator } from '../../models/form-control-validator.enum';

export class FormControlValidators {
  static afterDate(
    comparison: Date | string | UntypedFormControl,
    errorMessage: string,
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const controlDate: Date = new Date(control.value);
      const comparisonDate: Date =
        comparison instanceof UntypedFormControl
          ? new Date(comparison.value)
          : new Date(comparison);
      if (!isValid(controlDate) || !isValid(comparisonDate)) {
        return null;
      } else if (isBefore(controlDate, comparisonDate)) {
        return this.generateValidationError(
          FormControlValidator.AfterDate,
          errorMessage,
        );
      }
      return null;
    };
  }

  static alphabetical(errorMessage: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (
        this.controlHasValue(control) &&
        !ALPHABETICAL_REG_EXP.test(control.value)
      ) {
        return this.generateValidationError(
          FormControlValidator.Alphabetical,
          errorMessage,
        );
      }
      return null;
    };
  }

  static beforeDate(
    comparison: Date | string | UntypedFormControl,
    errorMessage: string,
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const controlDate: Date = new Date(control.value);
      const comparisonDate: Date =
        comparison instanceof UntypedFormControl
          ? new Date(comparison.value)
          : new Date(comparison);

      if (!isValid(controlDate) || !isValid(comparisonDate)) {
        return null;
      } else if (isAfter(controlDate, comparisonDate)) {
        return this.generateValidationError(
          FormControlValidator.BeforeDate,
          errorMessage,
        );
      }
      return null;
    };
  }

  private static controlHasNoValue(control: AbstractControl): boolean {
    return hasNoValue(control.value);
  }

  private static controlHasValue(control: AbstractControl): boolean {
    return this.controlHasNoValue(control) === false;
  }

  static email(errorMessage: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (
        this.controlHasValue(control) &&
        !EMAIL_ADDRESS_REG_EXP.test(control.value)
      ) {
        return this.generateValidationError(
          FormControlValidator.Email,
          errorMessage,
        );
      }
      return null;
    };
  }

  private static generateValidationError(
    validatorKey: FormControlValidator,
    errorMessage: string,
  ): ValidationErrors {
    return { [validatorKey]: errorMessage };
  }

  static IBAN(errorMessage: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.controlHasValue(control) && !IBAN_REG_EXP.test(control.value)) {
        return this.generateValidationError(
          FormControlValidator.IBAN,
          errorMessage,
        );
      }
      return null;
    };
  }

  static maximum(
    maximum: number,
    errorMessage: string,
    inclusive?: boolean,
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.controlHasNoValue(control)) {
        return null;
      } else if (inclusive && Number(control.value) >= maximum) {
        return this.generateValidationError(
          FormControlValidator.Maximum,
          errorMessage,
        );
      } else if (!inclusive && Number(control.value) > maximum) {
        return this.generateValidationError(
          FormControlValidator.Maximum,
          errorMessage,
        );
      }
      return null;
    };
  }

  static maximumLength(
    maximumLength: number,
    errorMessage: string,
    inclusive?: boolean,
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isArray = Array.isArray(control.value);

      if (this.controlHasNoValue(control)) {
        return null;
      } else if (
        inclusive &&
        isArray &&
        control.value.filter((value) => hasValue(value)).length >= maximumLength
      ) {
        return this.generateValidationError(
          FormControlValidator.MaximumLength,
          errorMessage,
        );
      } else if (
        !inclusive &&
        isArray &&
        control.value.filter((value) => hasValue(value)).length > maximumLength
      ) {
        return this.generateValidationError(
          FormControlValidator.MaximumLength,
          errorMessage,
        );
      } else if (
        inclusive &&
        !isArray &&
        control.value.toString().length >= maximumLength
      ) {
        return this.generateValidationError(
          FormControlValidator.MaximumLength,
          errorMessage,
        );
      } else if (
        !inclusive &&
        !isArray &&
        control.value.toString().length > maximumLength
      ) {
        return this.generateValidationError(
          FormControlValidator.MaximumLength,
          errorMessage,
        );
      }
      return null;
    };
  }

  static minimum(
    minimum: number,
    errorMessage: string,
    inclusive?: boolean,
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.controlHasNoValue(control)) {
        return null;
      } else if (inclusive && Number(control.value) <= minimum) {
        return this.generateValidationError(
          FormControlValidator.Minimum,
          errorMessage,
        );
      } else if (!inclusive && Number(control.value) < minimum) {
        return this.generateValidationError(
          FormControlValidator.Minimum,
          errorMessage,
        );
      }
      return null;
    };
  }

  static minimumLength(
    minimumLength: number,
    errorMessage: string,
    inclusive?: boolean,
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isArray = Array.isArray(control.value);

      if (this.controlHasNoValue(control)) {
        return null;
      } else if (
        inclusive &&
        isArray &&
        control.value.filter((value) => hasValue(value)).length <= minimumLength
      ) {
        return this.generateValidationError(
          FormControlValidator.MinimumLength,
          errorMessage,
        );
      } else if (
        !inclusive &&
        isArray &&
        control.value.filter((value) => hasValue(value)).length < minimumLength
      ) {
        return this.generateValidationError(
          FormControlValidator.MinimumLength,
          errorMessage,
        );
      } else if (
        inclusive &&
        !isArray &&
        control.value.toString().length <= minimumLength
      ) {
        return this.generateValidationError(
          FormControlValidator.MinimumLength,
          errorMessage,
        );
      } else if (
        !inclusive &&
        !isArray &&
        control.value.toString().length < minimumLength
      ) {
        return this.generateValidationError(
          FormControlValidator.MinimumLength,
          errorMessage,
        );
      }
      return null;
    };
  }

  static pattern(pattern: RegExp, errorMessage: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.controlHasValue(control) && !pattern.test(control.value)) {
        return this.generateValidationError(
          FormControlValidator.Pattern,
          errorMessage,
        );
      }
      return null;
    };
  }

  static postcode(
    countryIsoNumber: CountryIsoNumber,
    errorMessage: string,
  ): ValidatorFn {
    const postalCodeFormat: RegExp = CountryPostalCodeFormat[countryIsoNumber];

    return (control: AbstractControl): ValidationErrors | null => {
      if (
        this.controlHasValue(control) &&
        !postalCodeFormat.test(control.value)
      ) {
        return this.generateValidationError(
          FormControlValidator.Postcode,
          errorMessage,
        );
      }
      return null;
    };
  }

  static restrictedCharacters(errorMessage: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (
        this.controlHasValue(control) &&
        RESTRICTED_CHARACTERS_REG_EXP.test(control.value)
      ) {
        return this.generateValidationError(
          FormControlValidator.RestrictedCharacters,
          errorMessage,
        );
      }
      return null;
    };
  }

  static required(errorMessage: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.controlHasNoValue(control)) {
        return this.generateValidationError(
          FormControlValidator.Required,
          errorMessage,
        );
      }
      return null;
    };
  }

  static requiredConditionally(
    condition: boolean,
    errorMessage: string,
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (condition && this.controlHasNoValue(control)) {
        return this.generateValidationError(
          FormControlValidator.Required,
          errorMessage,
        );
      }
      return null;
    };
  }

  static requiredDate(errorMessage: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const controlDate: Date = new Date(control.value);
      if (!isValid(controlDate)) {
        return this.generateValidationError(
          FormControlValidator.Required,
          errorMessage,
        );
      }
      return null;
    };
  }

  static requiredTrue(errorMessage: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value !== true) {
        return this.generateValidationError(
          FormControlValidator.Required,
          errorMessage,
        );
      }
      return null;
    };
  }

  static requiredTrueConditionally(
    condition: boolean,
    errorMessage: string,
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (condition && control.value !== true) {
        return this.generateValidationError(
          FormControlValidator.Required,
          errorMessage,
        );
      }
      return null;
    };
  }

  static telephone(
    countryIsoNumber: CountryIsoNumber,
    errorMessage: string,
  ): ValidatorFn {
    const telephoneFormat: RegExp = CountryTelephoneFormat[countryIsoNumber];

    return (control: AbstractControl): ValidationErrors | null => {
      if (
        this.controlHasValue(control) &&
        !telephoneFormat.test(control.value)
      ) {
        return this.generateValidationError(
          FormControlValidator.Telephone,
          errorMessage,
        );
      }
      return null;
    };
  }
}
